import React from "react";
import { useTranslation } from "react-i18next";
import {
  CIPPredictionRecommendationConfig,
  optimizationTargetOptions,
} from "../AdvancedSettings.config";
import SliderCard from "../../../../components/SliderCard";
import SliderCardRecommended from "../../../../components/SliderCardRecommended";
import WPHeading from "../../../../packages/WPHeading";
import IconWithTooltip from "../../../../common/styles/components/headings/IconWithTooltip";
import { getOptimizationTargetKey } from "../../../predict/predictUtils";
import RecommendedDropdownRow from "./RecommendedDropdownRow";


const NumericSliderRow = ({ apiKey, config, settings, defaultSettings, suggestedSettings, handleChange, showDivider = false }) => {
  const { t } = useTranslation();
  const recommended = suggestedSettings[apiKey] != null ? suggestedSettings[apiKey] : null;
  return (
    <SliderCard
      min={config.minValue}
      max={config.maxValue}
      label={t(`advanceSettings.${config.key}`)}
      isInteger={config.integer}
      inputValue={settings[apiKey] ?? defaultSettings[apiKey] ?? ""}
      recommended={recommended}
      showDivider={showDivider}
      leftLabel={config.leftLabelKey ? t(`advanceSettings.${config.leftLabelKey}`) : undefined}
      rightLabel={config.rightLabelKey ? t(`advanceSettings.${config.rightLabelKey}`) : undefined}
      onChange={(val) =>
        handleChange({
          type: "slider",
          key: apiKey,
          value: val,
          min: config.ranges?.hard?.min ?? config.minValue,
          max: config.ranges?.hard?.max ?? config.maxValue,
        })
      }
    />
  );
};

/**
 * Renders a toggle switch row. If the config includes a `threshold` object,
 * a sub-slider is shown below the toggle — it becomes disabled when the toggle is ON
 * (meaning the system will auto-adapt the threshold instead of using the manual value).
 * If no threshold is defined (e.g. simulation_improve_accuracy), only the toggle is shown.
 */
const BooleanToggleRow = ({ apiKey, config, settings, defaultSettings, suggestedSettings, resetKey, handleChange }) => {
  const { t } = useTranslation();
  const isToggleOn = Boolean(settings[apiKey] ?? defaultSettings[apiKey]);
  const isRecommended = suggestedSettings[apiKey] != null;

  const sliderItems = config.threshold
    ? [
        {
          settingsKey: config.threshold.settingsKey,
          resetKey,
          labelKey: config.threshold.labelKey,
          minValue: config.threshold.minValue,
          maxValue: config.threshold.maxValue,
          isInteger: config.threshold.integer,
          inputValue: settings[config.threshold.settingsKey] ?? defaultSettings[config.threshold.settingsKey],
          recommended: suggestedSettings[config.threshold.settingsKey] ?? null,
          disabled: isToggleOn,
          leftLabel: config.threshold.leftLabelKey ? t(`advanceSettings.${config.threshold.leftLabelKey}`) : undefined,
          rightLabel: config.threshold.rightLabelKey ? t(`advanceSettings.${config.threshold.rightLabelKey}`) : undefined,
          onChange: (val) =>
            handleChange({
              type: "slider",
              key: config.threshold.settingsKey,
              value: val,
              min: config.threshold.ranges?.hard?.min ?? config.threshold.minValue,
              max: config.threshold.ranges?.hard?.max ?? config.threshold.maxValue,
            }),
        },
      ]
    : [];

  return (
    <SliderCardRecommended
      id={`toggle_${apiKey}`}
      recommendedLabel={t(`advanceSettings.${config.key}`)}
      recommendedValue={isRecommended ? (suggestedSettings[apiKey] ? t("ValueStrings.yes") : t("ValueStrings.no")) : null}
      isToggle={isToggleOn}
      onToggleChange={(checked) => handleChange({ type: "radio", key: apiKey, value: checked })}
      slider={sliderItems}
    />
  );
};


/**
 * Renders a toggle + two checkbox-controlled sliders (percentage & absolute thresholds).
 * Each slider has its own checkbox to enable/disable it independently.
 * - `checkboxDisabled: isToggleOn` — locks the checkboxes when the main toggle is ON
 *   (auto-adapt mode overrides all manual threshold selections).
 * - `disabled: isToggleOn || !isChecked` — locks the slider input if toggle is ON
 *   or if its own checkbox is unchecked.
 * The `cipOptionKey` maps each threshold to its unit-aware range from cipDynamicConfig.
 */
const CheckboxThresholdRow = ({ apiKey, config, settings, defaultSettings, suggestedSettings, resetKey, handleChange, cipDynamicConfig, pressure }) => {
  const { t } = useTranslation();
  const isToggleOn = Boolean(settings[apiKey] ?? defaultSettings[apiKey]);
  const isRecommended = suggestedSettings[apiKey] != null;

  const sliderItems = config.checkboxThresholds.map((threshold) => {
    const optionConfig = cipDynamicConfig.pressure_drop_threshold.options[threshold.cipOptionKey];
    const isChecked = settings[threshold.checkboxKey] ?? threshold.defaultChecked;
    return {
      settingsKey: threshold.settingsKey,
      resetKey,
      label: t(`advanceSettings.${optionConfig.labelKey}`).replace("<unit>", pressure ?? ""),
      minValue: optionConfig.minValue,
      maxValue: optionConfig.maxValue,
      isInteger: optionConfig.isInteger,
      inputValue: settings[threshold.settingsKey] ?? defaultSettings[threshold.settingsKey] ?? optionConfig.defaultValue,
      recommended: suggestedSettings[threshold.settingsKey] ?? null,
      showCheckbox: true,
      checked: isChecked,
      onCheckChange: (checked) => handleChange({ type: "radio", key: threshold.checkboxKey, value: checked }),
      disabled: isToggleOn || !isChecked,
      checkboxDisabled: isToggleOn,
      leftLabel: threshold.leftLabelKey ? t(`advanceSettings.${threshold.leftLabelKey}`) : undefined,
      rightLabel: threshold.rightLabelKey ? t(`advanceSettings.${threshold.rightLabelKey}`) : undefined,
      onChange: (val) =>
        handleChange({
          type: "slider",
          key: threshold.settingsKey,
          value: val,
          min: optionConfig.ranges?.hard?.min ?? optionConfig.minValue,
          max: optionConfig.ranges?.hard?.max ?? optionConfig.maxValue,
        }),
    };
  });

  return (
    <SliderCardRecommended
      id={`toggle_${apiKey}`}
      recommendedLabel={t(`advanceSettings.${config.key}`)}
      recommendedValue={isRecommended ? (suggestedSettings[apiKey] ? t("ValueStrings.yes") : t("ValueStrings.no")) : null}
      isToggle={isToggleOn}
      onToggleChange={(checked) => handleChange({ type: "radio", key: apiKey, value: checked })}
      subLabel={t(`advanceSettings.${config.subLabelKey}`)}
      slider={sliderItems}
    />
  );
};


const CIPPredictionRecommendation = ({
  resetKey,
  settings,
  suggestedSettings,
  handleChange,
  defaultSettings,
  cipDynamicConfig,
  pressure,
  excludeFields = [],
}) => {
  const { t } = useTranslation();

  const optimizationOptions = Object.values(optimizationTargetOptions).map((val) => ({
    label: t(`advanceSettings.${getOptimizationTargetKey(val)}`),
    value: val,
  }));

  /**
   * Common props passed to all row sub-components to avoid repetition.
   * `resetKey` is incremented on "Restore Defaults" to force SliderCard remounts
   * and reset their uncontrolled internal state.
   */
  const sharedProps = { settings, defaultSettings, suggestedSettings, resetKey, handleChange };

  return (
    <>
      <div className="info-section">
        <WPHeading fontNoto size="small" className="mt-24">
          {t("advanceSettings.cipPredictionAndRecommendation")}
        </WPHeading>
        <IconWithTooltip label={t("helpInformation.cipPredictionAndRecommendationHelp")} />
      </div>
      {/**
        * Iterates the config object in insertion order — which matches the desired
        * display order. Each entry's shape (type, isBoolean, checkboxThresholds)
        * determines which sub-component is rendered.
        */}
      {Object.entries(CIPPredictionRecommendationConfig.cipPredictionAndRecommendation)
        .filter(([apiKey]) => !excludeFields.includes(apiKey))
        .map(([apiKey, config], index, arr) => {
        if (config.type === "select") {
          const currentVal = settings[apiKey] ?? defaultSettings[apiKey];
          const suggestedVal = suggestedSettings[apiKey];
          /**
           * Converts the raw string value from settings into a `{label, value}` option
           * object that WPDropdown expects. Falls back to null if not found.
           */
          return (
            <RecommendedDropdownRow
              key={apiKey}
              label={t(`advanceSettings.${config.key}`)}
              value={optimizationOptions.find((o) => o.value === currentVal) ?? null}
              options={optimizationOptions}
              onChange={(option) => handleChange({ type: "radio", key: apiKey, value: option?.value })}
              recommendedLabel={suggestedVal != null ? t(`advanceSettings.${getOptimizationTargetKey(suggestedVal)}`) : null}
            />
          );
        }

        if (config.checkboxThresholds) {
          return (
            <div key={apiKey} className="slider-card-container">
              <CheckboxThresholdRow apiKey={apiKey} config={config} cipDynamicConfig={cipDynamicConfig} pressure={pressure} {...sharedProps} />
            </div>
          );
        }

        if (config.isBoolean) {
          return <BooleanToggleRow key={apiKey} apiKey={apiKey} config={config} {...sharedProps} />;
        }

        const nextEntry = arr[index + 1];
        const nextIsNumeric = nextEntry && !nextEntry[1].isBoolean && !nextEntry[1].type && !nextEntry[1].checkboxThresholds;
        return (
          <div key={`${apiKey}-${resetKey}`} className="slider-card-container">
            <NumericSliderRow apiKey={apiKey} config={config} showDivider={nextIsNumeric} {...sharedProps} />
          </div>
        );
      })}
    </>
  );
};

export default CIPPredictionRecommendation;
